import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type {
  ThirdPartyRegisterResponse,
  ThirdPartyResponseRow,
} from '@risksmart-app/trpc/src/types';
import type { GetThirdPartiesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

function mapTrpcThirdPartyToGraphQL(
  thirdParty: ThirdPartyResponseRow
): GetThirdPartiesQuery['third_party'][number] {
  return {
    ...thirdParty,
  };
}

/**
 * Maps TRPC third-party data to match the GraphQL query structure
 */
export function mapTrpcThirdPartiesToGraphQL(
  trpcData: ThirdPartyRegisterResponse | undefined
): GetThirdPartiesQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    third_party: trpcData.third_party.map((thirdParty) =>
      mapTrpcThirdPartyToGraphQL(thirdParty)
    ),
  };
}

export const useGetThirdPartyRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.thirdParty.register.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcThirdPartiesToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
